/**
 * @zendir/ui - Card Accent System
 *
 * Provides accent color configuration for cards and containers in space operations UIs.
 * Maps space system domains to appropriate accent colors for visual organization.
 *
 * Features:
 * - Predefined accent colors for common use cases
 * - Space system keyword → color mapping for auto-coloring
 * - Mix mode for automatic per-domain coloring
 * - React context and hook for easy integration
 *
 * @example
 * ```tsx
 * import {
 *   CardAccentProvider,
 *   useCardAccent,
 *   getSystemAccentColor
 * } from '@zendir/ui';
 *
 * // Wrap your app with the provider
 * <CardAccentProvider accentMode="mix">
 *   <MyApp />
 * </CardAccentProvider>
 *
 * // Use in components
 * const { getAccentColor } = useCardAccent();
 * <Container accentColor={getAccentColor('Power Systems')} />
 * ```
 */

import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";

// =============================================================================
// Types
// =============================================================================

export type CardAccentKey =
  | "cyan"
  | "electric"
  | "purple"
  | "teal"
  | "prussianBlue"
  | "green"
  | "amber"
  | "mix";

export interface CardAccentColor {
  /** Display label */
  label: string;
  /** Hex color value (or 'mix' for automatic) */
  value: string;
}

// =============================================================================
// Accent Color Definitions
// =============================================================================

/**
 * Predefined card accent colors
 * These are optimized for dark themes and space operations UIs
 */
export const CARD_ACCENT_COLORS: Record<CardAccentKey, CardAccentColor> = {
  cyan: { label: "Cyan", value: "#00c8ff" },
  electric: { label: "Electric", value: "#4d7cff" },
  purple: { label: "Purple", value: "#a855f7" },
  teal: { label: "Teal", value: "#14b8a6" },
  prussianBlue: { label: "Prussian Blue", value: "#1e3a5f" },
  green: { label: "Green", value: "#22c55e" },
  amber: { label: "Amber", value: "#f59e0b" },
  mix: { label: "Mix (Per System)", value: "mix" },
} as const;

/**
 * Space system keyword → accent color mapping
 * Used in 'mix' mode to automatically assign colors based on content
 */
export const SPACE_SYSTEM_COLORS: Record<string, string> = {
  // === Telemetry & Data ===
  telemetry: "#00c8ff",
  data: "#00c8ff",
  preview: "#00c8ff",
  stream: "#00c8ff",

  // === Power Systems ===
  power: "#22c55e",
  battery: "#22c55e",
  solar: "#22c55e",
  voltage: "#22c55e",
  energy: "#22c55e",

  // === Thermal Systems ===
  thermal: "#f59e0b",
  temperature: "#f59e0b",
  heatmap: "#f59e0b",
  heater: "#f59e0b",

  // === Attitude & Control ===
  attitude: "#a855f7",
  control: "#a855f7",
  orientation: "#a855f7",
  pointing: "#a855f7",
  gyro: "#a855f7",

  // === Communications ===
  spectrum: "#4d7cff",
  link: "#4d7cff",
  rf: "#4d7cff",
  downlink: "#4d7cff",
  uplink: "#4d7cff",
  packet: "#4d7cff",
  antenna: "#4d7cff",
  comm: "#4d7cff",

  // === Navigation & Orbit ===
  orbit: "#14b8a6",
  eclipse: "#14b8a6",
  polar: "#14b8a6",
  heliocentric: "#14b8a6",
  maneuver: "#14b8a6",
  contact: "#14b8a6",
  ground: "#14b8a6",
  pass: "#14b8a6",

  // === Sensors & Instruments ===
  sensor: "#00c8ff",
  star: "#00c8ff",
  tracker: "#00c8ff",
  scatter: "#00c8ff",
  payload: "#00c8ff",

  // === Health & Status ===
  health: "#22c55e",
  gauge: "#22c55e",
  subsystem: "#22c55e",
  status: "#22c55e",

  // === Analytics & Visualization ===
  sankey: "#a855f7",
  treemap: "#4d7cff",
  sunburst: "#14b8a6",
  parallel: "#f59e0b",
  radar: "#00c8ff",
  graph: "#a855f7",
  candlestick: "#f59e0b",
  histogram: "#00c8ff",
  pie: "#a855f7",
  donut: "#a855f7",
  bubble: "#14b8a6",
  rose: "#f59e0b",

  // === 3D Visualizations ===
  "3d": "#4d7cff",
  scatter3d: "#00c8ff",
  bar3d: "#22c55e",
  surface3d: "#f59e0b",
  lines3d: "#a855f7",
  debris: "#f59e0b",
  constellation: "#4d7cff",
  collision: "#f59e0b",
  satellite: "#4d7cff",

  // === Default ===
  default: "#00c8ff",
} as const;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get accent color based on title/content and accent mode
 *
 * @param title - Content title or description to analyze
 * @param accentMode - Current accent mode (specific color or 'mix')
 * @returns Hex color string or undefined
 *
 * @example
 * ```tsx
 * getSystemAccentColor('Power Systems Overview', 'mix') // '#22c55e'
 * getSystemAccentColor('Power Systems', 'purple') // '#a855f7'
 * ```
 */
export function getSystemAccentColor(
  title: string,
  accentMode: CardAccentKey,
): string | undefined {
  if (accentMode !== "mix") {
    return CARD_ACCENT_COLORS[accentMode].value;
  }

  const lowerTitle = title.toLowerCase();
  for (const [keyword, color] of Object.entries(SPACE_SYSTEM_COLORS)) {
    if (lowerTitle.includes(keyword)) {
      return color;
    }
  }
  return SPACE_SYSTEM_COLORS.default;
}

/**
 * Get all accent color options for building UI selectors
 */
export function getAccentColorOptions(): Array<{
  key: CardAccentKey;
  label: string;
  value: string;
}> {
  return Object.entries(CARD_ACCENT_COLORS).map(([key, config]) => ({
    key: key as CardAccentKey,
    label: config.label,
    value: config.value,
  }));
}

// =============================================================================
// React Context & Provider
// =============================================================================

export interface CardAccentContextValue {
  /** Current accent mode */
  accentMode: CardAccentKey;
  /** Get accent color for a given title/content */
  getAccentColor: (title: string) => string | undefined;
  /** All available accent options */
  accentOptions: Array<{ key: CardAccentKey; label: string; value: string }>;
}

const CardAccentContext = createContext<CardAccentContextValue>({
  accentMode: "mix",
  getAccentColor: () => undefined,
  accentOptions: getAccentColorOptions(),
});

export interface CardAccentProviderProps {
  /** Accent mode to use */
  accentMode?: CardAccentKey;
  /** Children */
  children: ReactNode;
}

/**
 * Provider for card accent context
 * Wrap your app or section to provide consistent accent coloring
 */
export function CardAccentProvider({
  accentMode = "mix",
  children,
}: CardAccentProviderProps): React.ReactElement {
  const value = useMemo(
    (): CardAccentContextValue => ({
      accentMode,
      getAccentColor: (title: string) =>
        getSystemAccentColor(title, accentMode),
      accentOptions: getAccentColorOptions(),
    }),
    [accentMode],
  );

  return (
    <CardAccentContext.Provider value={value}>
      {children}
    </CardAccentContext.Provider>
  );
}

/**
 * Hook to access card accent context
 *
 * @example
 * ```tsx
 * const { getAccentColor, accentMode } = useCardAccent();
 * <Container accentColor={getAccentColor('Power Chart')} />
 * ```
 */
export function useCardAccent(): CardAccentContextValue {
  return useContext(CardAccentContext);
}

export default CardAccentProvider;
